import {
  isVideo,
  type Attachment,
  type Message,
  type Reaction,
  type ReplySnapshot,
  type RoomSummary,
  type User,
} from "@/chat/protocol"
import type { PendingMessage } from "@/chat/reducer"

/**
 * Turns the raw message stream into the rows a thread renders.
 *
 * Everything here is pure and display-only: it never changes message data, so
 * the same thread can be re-rendered from the same state at any time.
 */

/**
 * A message body is either prose or a fenced code block. Code gets a card of
 * its own so a pasted benchmark or stack trace stays readable.
 */
export type MessageBody =
  { kind: "text"; text: string } | { kind: "code"; lang: string; text: string }

const FENCE = /^```([A-Za-z0-9+#-]{0,20})\n([\s\S]*?)\n?```$/

/**
 * Recognises a message that is entirely one fenced code block. Anything else —
 * including prose with a fence in the middle — stays prose, so the rule is
 * predictable rather than clever.
 */
export function parseBody(text: string): MessageBody {
  const match = FENCE.exec(text.trim())
  if (!match) return { kind: "text", text }

  const body = match[2]
  if (!body.trim()) return { kind: "text", text }

  return { kind: "code", lang: match[1] || "text", text: body }
}

/**
 * A message that is nothing but a few emoji.
 *
 * Chat clients show those large and without a bubble: sent on their own they
 * read as a gesture rather than as a sentence, and at body size a single
 * emoji is too small to tell apart. The cap keeps it to that gesture — a
 * paragraph of emoji stays a paragraph.
 */
const EMOJI_PRESENT = /\p{Extended_Pictographic}|\p{Regional_Indicator}/u

/**
 * Emoji, the pieces that build them (skin tones, the zero-width joiner, the
 * "render as emoji" selector, flag halves) and whitespace. Deliberately not
 * `\p{Emoji_Component}`, which also matches plain digits — "5🔥" is a
 * sentence, not a gesture.
 */
const EMOJI_ONLY =
  /^(?:\p{Extended_Pictographic}|\p{Emoji_Modifier}|\p{Regional_Indicator}|\uFE0F|\u200D|\s)+$/u

/** How many emoji still count as a gesture. */
const EMOJI_ONLY_LIMIT = 3

export function isEmojiOnly(text: string): boolean {
  const trimmed = text.trim()
  if (!trimmed || !EMOJI_PRESENT.test(trimmed) || !EMOJI_ONLY.test(trimmed)) {
    return false
  }

  return countEmoji(trimmed) <= EMOJI_ONLY_LIMIT
}

/**
 * Emoji are several code points each — a flag is two, a family can be seven —
 * so counting characters would overcount them badly. Whitespace between them
 * is not one of the three.
 */
function countEmoji(text: string): number {
  const stripped = text.replace(/\s+/gu, "")

  if (typeof Intl.Segmenter !== "function") return [...stripped].length

  const segmenter = new Intl.Segmenter(undefined, { granularity: "grapheme" })
  return [...segmenter.segment(stripped)].length
}

/**
 * The other person in a direct room; null for a public room. If the reader is
 * somehow not a participant, the first participant stands in.
 */
export function directPeer(
  room: RoomSummary,
  selfId: string | null
): User | null {
  if (room.kind !== "direct") return null

  return room.participants.find((p) => p.id !== selfId) ?? null
}

/**
 * What to call a room on screen. The server names a direct room "a & b"; the
 * reader wants to see the other person, not themselves.
 */
export function roomDisplayName(
  room: RoomSummary,
  selfId: string | null
): string {
  return directPeer(room, selfId)?.name ?? room.name
}

/**
 * Names a set of attachments for a line of text: "📷 Фото", "🎥 3 видео",
 * "📎 2 вложения" when pictures and videos are mixed. Empty for none.
 */
export function attachmentsLabel(total: number, videos: number): string {
  if (total === 0) return ""
  if (videos === 0) return total === 1 ? "📷 Фото" : `📷 ${total} фото`
  if (videos === total) return total === 1 ? "🎥 Видео" : `🎥 ${total} видео`
  return `📎 ${total} ${total < 5 ? "вложения" : "вложений"}`
}

/**
 * One-line stand-in for a message in the room list. A message that is only
 * pictures or videos has no text to show, so they are named instead.
 */
export function previewText(message: Message): string {
  if (message.text) return message.text

  return attachmentsLabel(
    message.attachments.length,
    message.attachments.filter(isVideo).length
  )
}

/**
 * One line standing in for the quoted message above a reply. A picture-only
 * original has no text to quote, so it is named; a long one is cut, since the
 * quote is a pointer to the message rather than a copy of it.
 */
export function replyPreview(snapshot: ReplySnapshot): string {
  const text = snapshot.text.trim().replace(/\s+/g, " ")

  if (!text) {
    if (!snapshot.hasAttachments) return ""
    return snapshot.hasVideo ? "🎥 Видео" : "📷 Фото"
  }
  if (text.length <= REPLY_PREVIEW_LIMIT) return text

  return `${text.slice(0, REPLY_PREVIEW_LIMIT).trimEnd()}…`
}

/** How much of a quoted message the preview keeps. */
const REPLY_PREVIEW_LIMIT = 120

export function formatTime(at: number): string {
  return new Date(at).toLocaleTimeString("ru-RU", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  })
}

/** Short stamp for the room list: time today, weekday this week, else a date. */
export function formatListTime(at: number): string {
  const date = new Date(at)
  const now = new Date()

  if (date.toDateString() === now.toDateString()) return formatTime(at)

  const daysAgo = (now.getTime() - at) / 86_400_000
  if (daysAgo < 7) return date.toLocaleDateString("ru-RU", { weekday: "short" })

  return date.toLocaleDateString("ru-RU", { day: "numeric", month: "short" })
}

/**
 * The DOM id a message is anchored at, which is how a reply's quote jumps to
 * the message it quotes: the scroller's items are whole runs of messages, and
 * the target is usually in the middle of one.
 */
export function messageAnchorId(messageId: string): string {
  return `msg-${messageId}`
}

export type ThreadRow = {
  key: string
  /** The server id; null while the row is a pending send, which cannot be reacted to. */
  id: string | null
  userId: string
  name: string
  /** The message as typed, which is what a quote of it shows. */
  text: string
  at: number
  isMine: boolean
  /** False while the server has not yet acknowledged the message. */
  delivered: boolean
  /** Null when the message is only pictures. */
  body: MessageBody | null
  attachments: Attachment[]
  reactions: Reaction[]
  /** The quoted message this row answers, or null for an ordinary message. */
  replyTo: ReplySnapshot | null
  /** False when this row continues the previous speaker's run. */
  showAuthor: boolean
}

/** Consecutive messages from one person within this window share a byline. */
const GROUP_WINDOW_MS = 5 * 60 * 1000

/**
 * Builds a thread's rows: confirmed messages followed by any still-pending
 * sends, with consecutive messages from one person grouped under one byline.
 */
export function buildThread(
  messages: Message[],
  pending: PendingMessage[],
  selfId: string | null,
  selfName: string
): ThreadRow[] {
  type Row = {
    key: string
    id: string | null
    userId: string
    name: string
    text: string
    attachments: Attachment[]
    reactions: Reaction[]
    replyTo: ReplySnapshot | null
    at: number
    delivered: boolean
  }

  const rows: Row[] = messages.map((m) => ({
    key: m.id,
    id: m.id,
    userId: m.userId,
    name: m.name,
    text: m.text,
    attachments: m.attachments,
    reactions: m.reactions,
    replyTo: m.replyTo,
    at: m.at,
    delivered: true,
  }))

  for (const p of pending) {
    rows.push({
      key: `pending:${p.nonce}`,
      id: null,
      userId: selfId ?? "self",
      name: selfName,
      text: p.text,
      attachments: p.attachments,
      reactions: [],
      replyTo: p.replyTo,
      at: p.at,
      delivered: false,
    })
  }

  return rows.map((row, index) => {
    const previous = rows[index - 1]

    const grouped =
      previous !== undefined &&
      previous.userId === row.userId &&
      row.at - previous.at < GROUP_WINDOW_MS

    return {
      key: row.key,
      id: row.id,
      userId: row.userId,
      name: row.name,
      text: row.text,
      at: row.at,
      isMine: row.userId === selfId,
      delivered: row.delivered,
      body: row.text ? parseBody(row.text) : null,
      attachments: row.attachments,
      reactions: row.reactions,
      replyTo: row.replyTo,
      showAuthor: !grouped,
    }
  })
}
